import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/api/client';
import ConfirmActionModal from '@/components/ConfirmActionModal';
import { AdminPageSections, CollapsiblePanel, StickyFormBar } from '@/components/common';
import PageHeader from '@/components/PageHeader';
import SourceDiagnosticsModal from '@/components/SourceDiagnosticsModal';
import SourceTestModal from '@/components/SourceTestModal';
import {
  DataTable,
  TruncatedCell,
  TableActionBar,
  TABLE_ACTION_MIN_WIDTH,
  tableActionsColumnClass,
  type DataTableColumn,
} from '@/components/table';
import { exportTableCsv } from '@/utils/exportTableCsv';
import { EmptyState, ErrorBanner, FilterSelect, SourceChips, StatusBadge, ToggleField, Toolbar } from '@/components/ui';
import { useToast } from '@/components/Toast';
import { useMe } from '@/context/MeContext';
import { planWarningMessage } from '@/utils/planWarning';
import {
  formatSuccessRate,
  formatTimestamp,
  sourceHealthDetailHint,
  sourceHealthLabel,
  sourceHealthTitle,
  sourceHealthVariant,
} from '@/utils/sourceHealth';
import type { Category, Source, SourceHealth } from '@/types';

const PANEL_SCOPE = 'sources-page';

type SourceFormState = {
  name: string;
  base_url: string;
  category: string;
  max_qps: string;
  max_concurrent: string;
  cache_ttl: string;
  robots_enabled: boolean;
  requires_search: boolean;
};

const EMPTY_FORM: SourceFormState = {
  name: '',
  base_url: '',
  category: 'vehicles',
  max_qps: '',
  max_concurrent: '',
  cache_ttl: '',
  robots_enabled: true,
  requires_search: true,
};

function policyFromForm(form: SourceFormState): Record<string, unknown> {
  const policy: Record<string, unknown> = {
    requires_search: form.requires_search,
    robots_enabled: form.robots_enabled,
  };
  if (form.max_qps.trim()) policy.max_qps = Number(form.max_qps);
  if (form.max_concurrent.trim()) policy.max_concurrent = Number(form.max_concurrent);
  if (form.cache_ttl.trim()) policy.cache_ttl = Number(form.cache_ttl);
  return policy;
}

function formFromSource(s: Source): SourceFormState {
  const p = s.policy_json || {};
  return {
    name: s.name,
    base_url: s.base_url,
    category: s.category,
    max_qps: s.max_qps != null ? String(s.max_qps) : p.max_qps != null ? String(p.max_qps) : '',
    max_concurrent:
      s.max_concurrent != null ? String(s.max_concurrent) : p.max_concurrent != null ? String(p.max_concurrent) : '',
    cache_ttl: p.cache_ttl != null ? String(p.cache_ttl) : '',
    robots_enabled: s.robots_enabled ?? p.robots_enabled !== false,
    requires_search: s.requires_search ?? p.requires_search !== false,
  };
}

export default function SourcesPage() {
  const { toast } = useToast();
  const { me } = useMe();
  const readOnly = me?.permissions.readOnly ?? true;
  const canManage = me?.permissions.canManageSources ?? false;
  const [sources, setSources] = useState<Source[]>([]);
  const [healthByName, setHealthByName] = useState<Record<string, SourceHealth>>({});
  const [categories, setCategories] = useState<Category[]>([]);
  const [editId, setEditId] = useState<string | null>(null);
  const [editSource, setEditSource] = useState<Source | null>(null);
  const [diagSource, setDiagSource] = useState<Source | null>(null);
  const [testSource, setTestSource] = useState<Source | null>(null);
  const [enabledFilter, setEnabledFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState<SourceFormState>(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<Source | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = useCallback(async () => {
    const [s, h, c] = await Promise.all([
      api.sources.list(),
      api.sources.health(),
      api.categories(),
    ]);
    setSources(s);
    setHealthByName(Object.fromEntries(h.map((row) => [row.source_name, row])));
    setCategories(c);
  }, []);

  const healthSummary = useMemo(() => {
    const values = Object.values(healthByName);
    return {
      blocked: values.filter((v) => v.status === 'BLOCKED').length,
      failing: values.filter((v) => v.status === 'FAILING').length,
      cooldown: values.filter((v) => v.status === 'COOLDOWN').length,
    };
  }, [healthByName]);

  const filteredSources = useMemo(
    () =>
      sources.filter((s) => {
        if (enabledFilter === 'enabled' && !s.is_enabled) return false;
        if (enabledFilter === 'disabled' && s.is_enabled) return false;
        if (categoryFilter && s.category !== categoryFilter) return false;
        return true;
      }),
    [sources, enabledFilter, categoryFilter]
  );

  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, [load]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const body = {
      name: form.name,
      base_url: form.base_url,
      category: form.category,
      policy_json: policyFromForm(form),
    };
    try {
      if (editId) {
        const { planWarning } = await api.sources.update(editId, {
          base_url: body.base_url,
          category: body.category,
          policy_json: body.policy_json,
        });
        const warn = planWarningMessage(planWarning);
        if (warn) toast(warn, 'error');
        toast('Source updated', 'success');
      } else {
        const { planWarning } = await api.sources.create(body);
        const warn = planWarningMessage(planWarning);
        if (warn) toast(warn, 'error');
        toast('Source created', 'success');
      }
      setForm(EMPTY_FORM);
      setEditId(null);
      setEditSource(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  }

  function startEdit(s: Source) {
    setEditId(s.id);
    setEditSource(s);
    setForm(formFromSource(s));
  }

  const toggle = useCallback(
    async (s: Source) => {
      const { planWarning } = await api.sources.update(s.id, { is_enabled: !s.is_enabled });
      const warn = planWarningMessage(planWarning);
      if (warn) toast(warn, 'error');
      toast(s.is_enabled ? `${s.name} disabled — scheduler will skip it` : `${s.name} enabled`, 'success');
      await load();
    },
    [toast, load]
  );

  const executeDelete = async () => {
    const s = deleteTarget;
    if (!s) return;
    setDeleteBusy(true);
    try {
      await api.sources.delete(s.id);
      toast('Source removed', 'success');
      if (editId === s.id) {
        setEditId(null);
        setEditSource(null);
        setForm(EMPTY_FORM);
      }
      setError('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleteBusy(false);
      setDeleteTarget(null);
    }
  };

  const sourceColumns = useMemo<DataTableColumn<Source>[]>(
    () => [
      {
        id: 'name',
        header: 'Source',
        width: 140,
        sortable: true,
        sortValue: (s) => s.name,
        csvValue: (s) => s.name,
        render: (s) => (
          <SourceChips sources={[s.name]} enabledBySource={{ [s.name]: s.is_enabled }} variant="compact" />
        ),
      },
      {
        id: 'base_url',
        header: 'Base URL',
        width: 180,
        csvValue: (s) => s.base_url,
        render: (s) => <TruncatedCell value={s.base_url} maxWidth={220} />,
      },
      {
        id: 'category',
        header: 'Category',
        width: 100,
        sortable: true,
        sortValue: (s) => s.category,
        csvValue: (s) => s.category,
        render: (s) => s.category,
      },
      {
        id: 'enabled',
        header: 'Enabled',
        width: 110,
        render: (s) =>
          canManage ? (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => void toggle(s)}
              title={s.is_enabled ? 'Disable source' : 'Enable source'}
            >
              <StatusBadge variant={s.is_enabled ? 'success' : 'warning'}>
                {s.is_enabled ? 'Enabled' : 'Disabled'}
              </StatusBadge>
            </button>
          ) : (
            <StatusBadge variant={s.is_enabled ? 'success' : 'warning'}>
              {s.is_enabled ? 'Enabled' : 'Disabled'}
            </StatusBadge>
          ),
      },
      {
        id: 'fetch_mode',
        header: 'Fetch',
        width: 90,
        render: (s) => s.fetch_mode || '—',
      },
      {
        id: 'max_qps',
        header: 'Rate limit',
        width: 90,
        render: (s) => (s.max_qps != null ? `${s.max_qps} QPS` : 'default'),
      },
      {
        id: 'robots',
        header: 'Robots',
        width: 70,
        render: (s) => (s.robots_enabled === false ? 'Off' : 'On'),
      },
      {
        id: 'timeout',
        header: 'Timeout',
        width: 80,
        render: (s) => (s.http_timeout_seconds != null ? `${s.http_timeout_seconds}s` : '—'),
      },
      {
        id: 'retry',
        header: 'Retry',
        width: 70,
        render: (s) => s.retry_max_attempts ?? '—',
      },
      {
        id: 'cooldown',
        header: 'Cooldown',
        width: 140,
        render: (s) => {
          const health = healthByName[s.name];
          if (health?.cooldown_until) {
            return <TruncatedCell value={formatTimestamp(health.cooldown_until)} />;
          }
          if (health?.status === 'COOLDOWN' || health?.status === 'BLOCKED') {
            return <span className="text-warning">Active</span>;
          }
          return '—';
        },
      },
      {
        id: 'health',
        header: 'Health',
        width: 160,
        render: (s) => {
          const health = healthByName[s.name];
          return health ? (
            <StatusBadge variant={sourceHealthVariant(health)} title={sourceHealthTitle(health)}>
              {sourceHealthLabel(health)}
            </StatusBadge>
          ) : (
            <span className="muted">—</span>
          );
        },
      },
      {
        id: 'links24h',
        header: 'Links 24h',
        width: 80,
        render: (s) => healthByName[s.name]?.links_24h ?? '—',
      },
      {
        id: 'successRate',
        header: 'Success rate',
        width: 90,
        render: (s) => formatSuccessRate(healthByName[s.name]?.success_rate_24h),
      },
      {
        id: 'lastSuccess',
        header: 'Last success',
        width: 140,
        render: (s) => <TruncatedCell value={formatTimestamp(healthByName[s.name]?.last_success_at)} />,
      },
      {
        id: 'lastFailure',
        header: 'Last failure',
        width: 140,
        render: (s) => <TruncatedCell value={formatTimestamp(healthByName[s.name]?.last_failure_at)} />,
      },
      {
        id: 'currentIssue',
        header: 'Current issue',
        width: 200,
        render: (s) => {
          const health = healthByName[s.name];
          const currentIssue = health
            ? health.current_error_message || health.info_message || sourceHealthDetailHint(health)
            : null;
          return <TruncatedCell value={currentIssue ?? '—'} />;
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        width: TABLE_ACTION_MIN_WIDTH.sources,
        minWidth: TABLE_ACTION_MIN_WIDTH.sources,
        resizable: false,
        actions: true,
        className: tableActionsColumnClass('sources'),
        render: (s) => (
          <TableActionBar
            danger={
              canManage ? (
                <button type="button" className="btn btn-sm btn-danger" onClick={() => setDeleteTarget(s)}>
                  Remove
                </button>
              ) : undefined
            }
          >
            <button type="button" className="btn btn-sm" onClick={() => setDiagSource(s)}>
              Diagnostics
            </button>
            {canManage ? (
              <button type="button" className="btn btn-sm" onClick={() => setTestSource(s)}>
                Test scrape
              </button>
            ) : null}
            {canManage && (
              <button type="button" className="btn btn-sm" onClick={() => startEdit(s)}>
                Edit
              </button>
            )}
          </TableActionBar>
        ),
      },
    ],
    [canManage, healthByName, toggle]
  );

  return (
    <div className="admin-page admin-page--sources">
      <PageHeader title="Sources" subtitle="Sites to scrape — health, rate limits, and test fetch" />

      {error ? <ErrorBanner message={error} onDismiss={() => setError('')} /> : null}

      {(healthSummary.blocked > 0 || healthSummary.failing > 0 || healthSummary.cooldown > 0) && (
        <div className="alert" role="status">
          {healthSummary.cooldown > 0 && (
            <span>
              {healthSummary.cooldown} source{healthSummary.cooldown === 1 ? '' : 's'} in cooldown (403/429/rate limit).
            </span>
          )}
          {healthSummary.cooldown > 0 && (healthSummary.blocked > 0 || healthSummary.failing > 0) && ' '}
          {healthSummary.blocked > 0 && (
            <span>
              {healthSummary.blocked} source{healthSummary.blocked === 1 ? '' : 's'} blocked by anti-bot or access
              controls.
            </span>
          )}
          {healthSummary.blocked > 0 && healthSummary.failing > 0 && ' '}
          {healthSummary.failing > 0 && (
            <span>
              {healthSummary.failing} source{healthSummary.failing === 1 ? '' : 's'} failing repeatedly.
            </span>
          )}
        </div>
      )}

      <AdminPageSections>
        {readOnly && !canManage && (
          <p className="muted">Read-only view of sources assigned to your account.</p>
        )}

        {canManage && (
          <CollapsiblePanel
            id="create-source"
            storageScope={PANEL_SCOPE}
            title={editId ? 'Edit source' : 'Create source'}
            subtitle="Runtime-configurable fields only — fetch mode and global timeout require deploy"
            defaultExpanded
          >
            <form className="sources-toolbar" onSubmit={save}>
              <div className="admin-form-grid admin-form-grid--sources">
                <label>
                  Name
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    disabled={!!editId}
                    pattern="[a-z][a-z0-9_]*"
                    title="Lowercase letters, digits, and underscores"
                  />
                </label>
                <label className="admin-form-grid__span-2">
                  Base URL
                  <input
                    value={form.base_url}
                    onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                    required
                    type="url"
                  />
                </label>
                <label>
                  Category
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Max QPS
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="10"
                    value={form.max_qps}
                    onChange={(e) => setForm({ ...form, max_qps: e.target.value })}
                    placeholder="Default"
                  />
                </label>
                <label>
                  Max concurrent
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={form.max_concurrent}
                    onChange={(e) => setForm({ ...form, max_concurrent: e.target.value })}
                    placeholder="Default"
                  />
                </label>
                <label>
                  Cache TTL (seconds)
                  <input
                    type="number"
                    min="0"
                    value={form.cache_ttl}
                    onChange={(e) => setForm({ ...form, cache_ttl: e.target.value })}
                    placeholder="Default"
                  />
                </label>
                <ToggleField
                  id="source-robots-enabled"
                  checked={form.robots_enabled}
                  onChange={(next) => setForm({ ...form, robots_enabled: next })}
                  label="Respect robots.txt"
                  className="admin-form-grid__full"
                />
                <ToggleField
                  id="source-requires-search-text"
                  checked={form.requires_search}
                  onChange={(next) => setForm({ ...form, requires_search: next })}
                  label="Requires search text"
                  hint="Skip this source when a subscription has no search text configured."
                  className="admin-form-grid__full"
                />
                {editSource ? (
                  <div className="admin-form-grid__full source-form-readonly">
                    <p className="admin-form-field__hint">Read-only (set in code / global config)</p>
                    <dl className="detail-grid">
                      <dt>Fetch mode</dt>
                      <dd>{editSource.fetch_mode || '—'}</dd>
                      <dt>HTTP timeout</dt>
                      <dd>{editSource.http_timeout_seconds != null ? `${editSource.http_timeout_seconds}s` : '—'}</dd>
                      <dt>Retry attempts</dt>
                      <dd>{editSource.retry_max_attempts ?? '—'}</dd>
                      <dt>Retry backoff base</dt>
                      <dd>{editSource.retry_backoff_base ?? '—'}</dd>
                    </dl>
                  </div>
                ) : null}
              </div>
              <StickyFormBar>
                <button type="submit" className="btn btn-primary">
                  {editId ? 'Save changes' : 'Create source'}
                </button>
                {editId && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setEditId(null);
                      setEditSource(null);
                      setForm(EMPTY_FORM);
                    }}
                  >
                    Cancel
                  </button>
                )}
              </StickyFormBar>
            </form>
          </CollapsiblePanel>
        )}

        <CollapsiblePanel
          id="source-list"
          storageScope={PANEL_SCOPE}
          title="All sources"
          badge={filteredSources.length}
          defaultExpanded
          unmountWhenCollapsed
        >
          <Toolbar>
            <FilterSelect label="Enabled" htmlFor="sources-enabled-filter">
              <select
                id="sources-enabled-filter"
                className="filter-select"
                value={enabledFilter}
                onChange={(e) => setEnabledFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="enabled">Enabled only</option>
                <option value="disabled">Disabled only</option>
              </select>
            </FilterSelect>
            <FilterSelect label="Category" htmlFor="sources-category-filter">
              <select
                id="sources-category-filter"
                className="filter-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </FilterSelect>
          </Toolbar>

          {filteredSources.length === 0 ? (
            <EmptyState title="No sources match" description="Adjust filters or create a new source." />
          ) : (
            <DataTable
              columns={sourceColumns}
              rows={filteredSources}
              getRowKey={(s) => s.id}
              columnStorageKey="sources"
              toolbar={{
                searchPlaceholder: 'Filter sources…',
                onRefresh: () => load().catch((e) => setError(e.message)),
                onExportCsv: () => exportTableCsv(sourceColumns, filteredSources, 'sources.csv'),
              }}
            />
          )}
        </CollapsiblePanel>
      </AdminPageSections>

      {diagSource && (
        <SourceDiagnosticsModal
          source={diagSource}
          health={healthByName[diagSource.name]}
          onClose={() => setDiagSource(null)}
        />
      )}

      {testSource && canManage && (
        <SourceTestModal source={testSource} onClose={() => setTestSource(null)} />
      )}

      <ConfirmActionModal
        open={Boolean(deleteTarget)}
        title="Delete source?"
        description={
          deleteTarget ? (
            <>
              Delete source <strong>{deleteTarget.name}</strong>? This cannot be undone.
            </>
          ) : undefined
        }
        confirmLabel="Delete"
        variant="danger"
        loading={deleteBusy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={executeDelete}
      />
    </div>
  );
}
